"use client";
import {
  ATOM_frontendEnv,
  ATOM_frontendEnv_Sync,
} from "@lattice/frontend/lib/jotai/env.atom";
import { ThemeProvider } from "./ThemeProvider";
import { TRPCProvider } from "./TRPCProvider";
import { Provider as JotaiProvider } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import React from "react";
import type { T_FrontendEnv } from "@lattice/frontend/lib/env/env";
import { ATOM_auth } from "@lattice/frontend/lib/jotai/auth.atom";
import { getStore } from "@lattice/frontend/lib/jotai/store";
import { ClientTraceInitializer } from "./ClientTraceInitializer";

type T_ProvidersProps = {
  frontendEnv: T_FrontendEnv;
  isAuthed: boolean;
};
export const Providers: React.FC<React.PropsWithChildren<T_ProvidersProps>> = ({
  children,
  frontendEnv,
  isAuthed,
}) => {
  const store = React.useMemo(() => getStore(), []);

  useHydrateAtoms(
    [
      [ATOM_frontendEnv, frontendEnv],
      [ATOM_frontendEnv_Sync, frontendEnv],
      [ATOM_auth, isAuthed],
    ],
    {
      store,
      // The app uses a singleton Jotai store so non-React clients can read env
      // flags. Force server-derived values into that store on every RSC payload;
      // otherwise auth can stay stale and hydrate a different branch than SSR.
      dangerouslyForceHydrate: true,
    },
  );

  return (
    <JotaiProvider store={store}>
      {frontendEnv.APP_ENVIRONMENT === "local" ? (
        <React.Suspense fallback={null}>
          <ClientTraceInitializer
            appEnvironment={frontendEnv.APP_ENVIRONMENT}
          />
        </React.Suspense>
      ) : null}
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        // enableSystem
        disableTransitionOnChange
      >
        <TRPCProvider>{children}</TRPCProvider>
      </ThemeProvider>
    </JotaiProvider>
  );
};
